<template>
	<div>
		<div class="white">
			<v-row class="ma-0" align="center">
				<!-- breadcrumbs -->
				<v-col lg="auto" md="12" cols="auto" class="d-flex flex-column pe-0">
					<slot name="breadcrumbs" :item="true" class="journal-breadcrumbs"></slot>
				</v-col>

				<v-col cols="auto" lg="auto" sm="12" md="auto" class="flex-grow-1 d-flex ps-lg-0">
					<v-layout wrap class="d-flex flex-row my-border rounded-lg">
						<!-- bond No (next/prev) -->						
						<v-col
							md="auto"
							cols="auto"
							style="width: auto !important;"
							class="d-flex align-center justify-start pe-0"
						>
							<bee-bond-up-down
								v-model="bond_no"
								:label="$t('AccountingJournalPage.bond-no')"
								:items="simplified_bonds.map(c=>c.bond_no)"
								class="medium-placeholder"
								@keydown-enter-prevent="fetchBond()"
								@programmatically-changed="fetchBond()"
							></bee-bond-up-down>
						</v-col>

						<!-- create & save/edit bond -->
						<v-col cols="auto" sm="auto" xs="12" md="auto" lg="auto" class="d-flex align-center">
							<!-- (save/edit) bond | show bill  -->
							<bee-btn
								:icon="bond.is_posted
									? (bond.invoice_id 
										? 'far fa-share-square' : 'fa-envelope-open') 
									: 'fa-envelope'
								"
								:outer-class="on_lgAndUp ? 'text-wrap' : ''" 
								:content="on_lgAndUp ?
									bond.is_posted ? bond.invoice_id ? $t('AccountingJournalPage.view-invoice') : $t('AccountingJournalPage.edit') 
									: $t('AccountingJournalPage.save')
									: ''
								"
								:disabled="file_is_locked && !bond.invoice_id"
								:color="bond.is_posted ? 'deletingColor' : 'blueGray'"
								@click="bond.is_posted 
									? ( bond.invoice_id ? reForwardToRelatedInvoice() : unPosting()) 
									: posting()
								"
								:loading="updateBondLoading"
								context="bond"
								:action-name="actions.EDIT"
							></bee-btn>

							<!-- create bond -->
							<bee-btn
								:content="$t('AccountingJournalPage.new')"
								context="bond"
								color="addingColor"
								icon="fa-plus"
								:disabled="file_is_locked"
								outer-class="ms-3"
								@click="create_bonds_manually ? addBondDialog = true : addBond()"
								:loading="loading"
								:action-name="actions.CREATE"
							></bee-btn>
						</v-col>

						<v-spacer v-if="on_mdAndUp"></v-spacer>

						<!-- create account button -->
						<v-col sm="auto" xs="12" cols="auto" class="d-flex align-center">
							<bee-btn
								context="account"
								:action-name="actions.CREATE"
								@click="createAccount()"
								:disabled="file_is_locked"
								color="addingColor"
								:content="$t('AccountingJournalPage.add')"
								icon="fa-user-plus"
							></bee-btn>
						</v-col>

						<v-spacer v-if="on_mdAndUp"></v-spacer>

						<!-- bond posting progress circular -->
						<v-col cols="auto" class="d-flex justify-lg-end py-1">
							<router-link style="text-decoration: none;" :to="{name: 'journals'}">
								<bee-progress-circular
									:size="60"
									:width="6"
									:title="bond_posting_ratio_caption"
									:value="bond_posting_ratio"
									:tooltip="bond_posting_ratio_tooltip"
								></bee-progress-circular>
							</router-link>
						</v-col>

						<!-- posted / not posted -->
						<v-col
							lg="1"
							sm="12"
							xs="12"
							:cols="on_mdAndUp ? 'auto': '12'"
							:class="[
								bond.is_posted ? 'postedColor' : 'unpostedColor',
								on_lgAndUp ? 'border-round-end' : 'border-round-bottom'
							]"
							:style="on_lgAndUp ? 'height: 100%; ' : ''"
							style="border-color: transparent !important;  align-items: center !important;"
							class="justify-content-center justify-center flex-grow-1 d-flex white--text my-border text--title text-center align-center font-weight-bold text-wrap"
						>
							<span class="text-center align-center" style="line-height: 1.2;">
								{{bond.is_posted ? '' : $t('AccountingJournalPage.not')}}
								<br v-if="!bond.is_posted" />
								{{$t('AccountingJournalPage.posted')}}
							</span>
						</v-col>
					</v-layout>
				</v-col>
			</v-row>
		</div>

		<div>
			<!-- table -->
			<v-card flat>
				<v-card-text class="py-0 px-3">
					<bee-handy-smart-table
						ref="table"
						v-model="tableInputs"
						:headers="headers"
						:items="entries"
						hot-inputs="lazy"
						:hot-inputs-order="['currency-price']"
						dense
						zebra
						fixed-header
						pagination-on-scroll
						stay-in-input-fields
						:data-table-props="{
							disableSort: true
						}"
						hide-shown-rows-text="auto"
						:shown-rows-text="$t('shown-rows-text')"
						:height="$vuetify.breakpoint.height - 243"
						hide-no-data-area
						hide-inputs-details="auto"
						with-columns-divider
						:show-add-row="!bond.is_posted && !file_is_locked && !updateBondLoading && !isViewer"
						:editable-rows="!bond.is_posted && !file_is_locked && !updateBondLoading && !isViewer"
						:context-menu-items="contextmenuItems"
						:loading="loading"
						:disable-input-fields="loading"
						:disable-context-menu="loading || isViewer"
						disable-mobile-context-menu
						:row-highlight-expression="(item) => item.id === Number($route.query.entryId)"
						:scroll-to-row-expression="(item) => item.id === entryId || item.id === entries[entries.length - 1].id"
						:validation-messages.sync="validationMessages"
						@context-menu-account-statement="openAccountStatement"
						@context-menu-edit="({ item }) => entryId = item.id"
						@context-menu-delete="({ item }) => { deleteEntryId = item.id; dialogDeleteAccountingEntry = true}"
						@edit-cancelled="
							entryId = null;
							$nextTick(() => {
								$refs.table.resetInputs({date: new Date().toISOString().slice(0, 10)});
							})
						"
						@keypress.enter="entryHandler"
						@keydown.tab="inputsNavigationHandler"
						@reached-last-input="entryHandler"
						@change-currency="tableInputs['currency-price'] = getCurrencyById(tableInputs.currency)?.latest_exchange_record??null"
					>
						<!-- account -->
						<template v-slot:input.account="{ on, attr }">
							<v-autocomplete
								v-on="on"
								v-bind="attr"
								:items="configs.accounts.filter(c => c.type === 2)"
								item-value="id"
								:item-text="item => item.accounting_no + '-'+ item.name"
								auto-select-first
							>
								<template v-slot:selection="{ item }">
									{{ item.accounting_no +'-'+ item.name }}
								</template>
								<template v-slot:item="{ item }">
									{{ item.accounting_no + '-'+ item.name }}
									<v-spacer></v-spacer>
									<bee-advanced-money-label
										:with-color="true"
										:value="item?.account_statement_summary"
										:show-zeros-values="true"
									></bee-advanced-money-label>
								</template>
							</v-autocomplete>
						</template>

						<!-- currency -->
						<template v-slot:input.currency="{ on, attr }">
							<v-autocomplete
								v-on="on"
								v-bind="attr"
								:items="[{ id: null, symbol: null }, ...configs.currencies]"
								item-value="id"
								item-text="symbol"
							>
								<template v-slot:item="{ item }">
									{{item.id ? item.symbol : $t('inputs.undefined')}}
								</template>
							</v-autocomplete>
						</template>

						<!-- order inside bond -->
						<template v-slot:no-input.order-inside-bond="{ item }">
							<span class="font-weight-bold">{{item ? item.order_inside_bond : null}}</span>
						</template>

						<!-- date -->
						<!-- FIXME replace `bee-date-field` with `bee-date-input` -->
						<template v-slot:input.date="{ on, attr }">
							<bee-date-field
								v-bind="attr"
								v-on="on"
								type="date"
							></bee-date-field>
						</template>

						<!-- hint -->
						<template v-slot:no-input.hint>
							<v-menu bottom v-if="validationMessages.length > 0">
								<template v-slot:activator="{ on, attr }">
									<v-icon
										v-on="on"
										v-bind="attr"
										color="danger-color"
										size="20"
									>mdi-information</v-icon>
								</template>

								<v-list>
									<v-list-item
										v-for="(message, i) in inputsValidationMessages"
										:key="i"
										dense
										class="px-2"
									>
										<v-list-item-icon class="me-0 mb-0">
											<v-icon color="danger-color" x-small>mdi-circle-half</v-icon>
										</v-list-item-icon>
										<v-list-item-title class="danger-color--text">
											{{message}}
										</v-list-item-title>
									</v-list-item>
								</v-list>
							</v-menu>
						</template>
					</bee-handy-smart-table>
				</v-card-text>
			</v-card>

			<!-- dialog delete Accounting -->
			<bee-dialog
				v-model="dialogDeleteAccountingEntry"
				:title="$t('AccountingJournalPage.delete-account-entry')"
				:message="$t('AccountingJournalPage.are-you-sure-you-want-to-delete-the-entry')"
				@handler="confirmDeleteEntry()"
				@close="dialogDeleteAccountingEntry = false; deleteEntryId = null"
			></bee-dialog>

			<!-- creat bond dialog -->
			<v-dialog transition="dialog-top-transition" v-model="addBondDialog" max-width="500">
				<v-card>
					<v-card-title class="headline">
						{{$t('AccountingJournalPage.add-bond')}}
					</v-card-title>
					<v-card-text>
						<bee-date-picker
							context="bond"
							:action-name="actions.CREATE"
							:label="$t('AccountingJournalPage.bond-date')"
							v-model="bond_date"
							:rules="[this.rules.date]"
						></bee-date-picker>
					</v-card-text>
					<v-card-actions class="mr-3">
						<v-btn color="greenDarken1" text @click="addBond()" :loading="loading">
							{{$t('AccountingJournalPage.create')}}
						</v-btn>
						<v-btn color="redDarken1" text @click="addBondDialog = false">
							{{$t('AccountingJournalPage.cancel')}}
						</v-btn>
						<v-spacer></v-spacer>
					</v-card-actions>
				</v-card>
			</v-dialog>
			
			<!-- creata account dialog -->
			<account-form v-model="showAccountForm"></account-form>
		</div>

		<!-- footer -->
		<v-bottom-navigation id="footer" height="auto" app horizontal class="px-3" background-color="#E1E2E4">
			<v-row class="fill-height ma-0" style="width: 100%" align="center">
				<template v-if="on_mdAndUp">
					<!-- sum -->
					<bee-label
						:content="$t('AccountingJournalPage.sum')"
						:bold="true"
						class="primary--text ps-4"
						style="width: 17%"
					></bee-label>
					
					<bee-advanced-money-label
						v-model="sum_debits"
						:bold="true"
						:with-color="true"
						:currencies="configs.currencies"
						class="mx-1 text-left"
						style="width: 12%"
					></bee-advanced-money-label>
					
					<bee-advanced-money-label
						v-model="sum_credits"
						:bold="true"
						:with-color="true"
						:currencies="configs.currencies"
						class="ms-4 text-right"
						style="width: 12%"
					></bee-advanced-money-label>
					
					<bee-outlined-header-info
						border-color="transparent"
						:head="$t('AccountingJournalPage.difference')"
						:column="!on_mdAndUp"
						style="width:18%; text-align: right !important;"
					>
						<template v-slot:content="{}">
							<bee-advanced-money-label
								empty-state="_"
								v-model="sum_difference"
								:bold="true"
								:with-color="true"
								:currencies="configs.currencies"
							></bee-advanced-money-label>
						</template>
					</bee-outlined-header-info>
				</template>
				
				<v-col v-else cols="auto" lg="2" class="ma-0 pa-0 d-flex justify-lg-end">
					<bee-outlined-header-info
					    border-color="transparent"
					    :head="$t('AccountingJournalPage.difference')"
					    :column="false"
					>
						<template v-slot:content="{}">
							<bee-advanced-money-label
								empty-state="_"
								v-model="sum_difference"
								:bold="true"
								:with-color="true"
								:currencies="configs.currencies"
							></bee-advanced-money-label>
						</template>
					</bee-outlined-header-info>
				</v-col>

				<template v-if="on_mdAndUp">
					<div style="width:9%" class="text-center" >
						<div
							v-for="currency in currency_debits? currency_debits.split(',') : []"
							v-bind:key="currency"
						>
							<bee-label
								:showOnlyNumbersPatch="true"
								class="text-center"
								color="red"
								:content="currency"
								:bold="true"
							></bee-label>
							<br />
						</div>
					</div>
					
					<div style="width:9%" class="text-right">
						<div
							v-for="currency in currency_credits? currency_credits.split(',') : []"
							v-bind:key="currency"
						>
							<bee-label
								:showOnlyNumbersPatch="true"
								class="text-center"
								color="green"
								:content="currency"
								:bold="true"
							></bee-label>
							<br />
						</div>
					</div>

					<v-spacer></v-spacer>
				</template>
				
				<v-col
					v-if="show_symbol_currencies && on_mdAndUp"
					cols="auto"
					class="ma-0 pe-1 d-flex justify-lg-end justify-md-end justify-center"
				>
					<v-menu
						v-model="showExchangeForm"
						top
						offset-y
						right
						transition="slide-y-transition"
						max-width="400"
						:close-on-click="false"
						:close-on-content-click="false"
					>
						<template v-slot:activator="{ on }">
							<v-btn
								class="pe-0"
								depressed
								color="transparent"
								v-on="on"
								@click="showExchangeForm = !showExchangeForm"
							>
								<v-icon class="mx-1">{{ showExchangeForm ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
								{{$t('AccountingJournalPage.currency-equivalent')}}
							</v-btn>
						</template>
						<v-card>
							<v-card-text class="py-1 px-2">
								<v-row align="center" class="ma-0 pa-0">
									<v-card-subtitle class="py-0">
										{{$t('AccountingJournalPage.select-currency-to-show-the-currency-equivalent')}}
									</v-card-subtitle>
									<v-spacer></v-spacer>
									<v-btn icon @click="showExchangeForm = !showExchangeForm">
										<v-icon>mdi-close</v-icon>
									</v-btn>
								</v-row>
								<v-row class="ma-0 pa-0">
									<v-col cols="12" lg="5" class="pe-lg-0">
										<bee-currencies-autocomplete
											item-text="symbol"
											:items="configs.currencies.filter(c=>c.id != configs.financial_file.default_currency_id) || []"
											v-model="sum_difference_equivalent.currency_id"
											@change="changeSumDifferenceEquivalent($event)"
										></bee-currencies-autocomplete>
									</v-col>
									<v-col class="px-lg-0">
										<bee-outlined-header-info border-color="transparent" :column="false">
											<template v-slot:head="{}">
												<bee-advanced-money-label
													v-model="sum_difference_equivalent"
													:bold="true"
													:with-color="true"
													:currencies="configs.currencies"
												></bee-advanced-money-label>
											</template>
										</bee-outlined-header-info>
									</v-col>
								</v-row>
							</v-card-text>
						</v-card>
					</v-menu>
				</v-col>
			</v-row>
		</v-bottom-navigation>
	</div>
</template>
<script>
import AccountForm from './sub-components/AccountForm.vue';
import store from '../../../store/index.js';
import ls from '../../../settings/localStorageExtension';
import _ from 'lodash';
import { mapGetters, mapState } from 'vuex';
import actions from '../../../permissions/actions-names.js';
import rules from '../../../validation';
import { bondType } from '@/helpers/enums';

const openNewWindowForAccountStatement =
	JSON.parse(localStorage.getItem('openAccountStatementInNewWindow'));

export default {
	components: { AccountForm },

	data: () => ({
		dialogDeleteAccountingEntry: false,
		actions,
		sum_difference_equivalent: {
			amount: null,
			currency_id: null,
			money_type: null
		},
		showAccountForm: false,
		showExchangeForm: false,
		externalRequest: false,
		restLastRow: false,
		bond_no: null,
		...rules,
		addBondDialog: false,
		loading: false,
		bond_date: new Date().toISOString().substr(0, 10).toString(),
		linkedBoundRules: {
			debit: true,
			credit: true,
			currency_debit: true,
			currency_credit: true,
			currency_id: true
		},

		// helpers
		updateBondLoading: false,
		deleteEntryId: null,
		entryId: null,
		saveNotes: null,
		tableInputs: {},
		validationMessages: [],
	}),

	watch: {
		// $route: function(to, from) {
		$route: function(to) {
			this.sum_difference_equivalent = {
				amount: null,
				currency_id: null,
				money_type: null
			};
			// || (from.name == "accounting-journal" && to.name == "accounting-journals")
			if (
				!to.params.id ||
				to.params.id != this.simplified_bonds?.find(c => c.bond_no == this.bond_no)?.id
			) {
				if (this.simplified_bonds.length > 0) {
					const bondId = this.simplified_bonds[this.simplified_bonds.length - 1].id
					this.loading = true;
					store
						.dispatch("bonds/fetch", {
							bondId: bondId,
							bondType: bondType.accounting
						})
						.then(response => {
							if (!to.params.id) {
								this.$router.replace({ name: this.$route.name, params: { id: bondId } })
							}
							this.bond_no = response.bond_no;
							this.loading = false;
						});
				}
			}
		},

		'tableInputs.note'(newVal) {
			if (!this.entryId) {
				this.saveNotes = newVal;
			}
		}
	},

	computed: {
		// table headers
		headers0(){
			const headers = [
				{
					text: this.$t('AccountingJournalPage.headers.sequence'),
					name: 'order-inside-bond',
					value: 'order_inside_bond',
					width: '5%',
					noInput: true
				},
				{
					text: this.$t('AccountingJournalPage.headers.debit'),
					name: 'debit',
					value: 'debit.amount',
					type: 'number-format',
					width: '8%',
					inputRules: !this.tableInputs.credit ? [this.rules.required] : [],
					setInputExpr: (inputs, activeInput) =>
						inputs.debit > 0 && activeInput === 'credit' && inputs.credit > 0 ? 0 : inputs.debit,
				},
				{
					text: this.$t('AccountingJournalPage.headers.credit'),
					name: 'credit',
					value: 'credit.amount',
					type: 'number-format',
					width: '8%',
					inputRules: !this.tableInputs.debit ? [this.rules.required] : [],
					setInputExpr: (inputs, activeInput) =>
						inputs.credit > 0 && activeInput === 'debit' && inputs.debit > 0 ? 0 : inputs.credit,
				},
				{
					text: this.$t('AccountingJournalPage.headers.account'),
					value: 'account_id',
					name: 'account',
					width: '16%',
					setValueExpr: (value) => {
						const account = this.getAccountById(value) || {};
						return `${account.accounting_no}-${account.name}`
					},
					inputRules: [this.rules.required]
				},
				{
					text: this.$t('AccountingJournalPage.headers.note'),
					name: 'note',
					value: 'note',
					width: '17%',
				},
				{
					text: this.$t('AccountingJournalPage.headers.currency'),
					name: 'currency',
					value: 'currency_id',
					width: 100,
					setValueExpr: (value) => (this.getCurrencyById(value) || {}).symbol,
				},
				{
					text: this.$t('AccountingJournalPage.headers.currency-debit'),
					name: 'currency-debit',
					value: 'currency_debit.amount',
					type: 'number-format',
					width: this.$emerald.ifLang('es', '15%', '6%'),
					inputRules:
						this.tableInputs.currency && !this.tableInputs['currency-credit']
							? [this.rules.required]
							: [],
					setInputExpr: (inputs, activeInput) => {
						if ((activeInput === 'currency-credit' && inputs['currency-debit'] > 0) || !inputs.currency) {
							return 0;
						} else if (activeInput === 'currency-debit') {
							if (inputs.currency) {
								this.tableInputs.debit = inputs['currency-price'] * inputs['currency-debit'];
								if (this.tableInputs.credit) this.tableInputs.credit = null;
							}
							return inputs['currency-debit'];
						} else {
							return inputs.currency ? inputs.debit / (inputs['currency-price'] || 1) : null;
						}
					}
				},
				{
					text: this.$t('AccountingJournalPage.headers.currency-credit'),
					name: 'currency-credit',
					value: 'currency_credit.amount',
					type: 'number-format',
					width: this.$emerald.ifLang('es', '15%', '6%'),
					inputRules:
						this.tableInputs.currency && !this.tableInputs['currency-debit']
							? [this.rules.required]
							: [],
					setInputExpr: (inputs, activeInput) => {
						if ((activeInput === 'currency-debit' && inputs['currency-credit'] > 0) || !inputs.currency) {
							return 0;
						} else if (activeInput === 'currency-credit') {
							if (inputs.currency) {
								this.tableInputs.credit = inputs['currency-price'] * inputs['currency-credit'];
								if (this.tableInputs.debit) this.tableInputs.debit = null;
							}
							return inputs['currency-credit'];
						} else {
							return inputs.currency ? inputs.credit / (inputs['currency-price'] || 1) : null;
						}
					}
				},
				{
					text: this.$t('AccountingJournalPage.headers.price'),
					name: 'currency-price',
					value: 'currency_price',
					width: '8%',
					type: 'number-format',
					settings: { itemText: 'equivalentPrice', context: 'entry', actionName: this.actions.GET },
					inputRules: this.tableInputs.currency ? [this.rules.required] : [],
					setInputExpr: (inputs, activeInput) => {
						if (!inputs.currency) {
							return 0;
						} else if (activeInput === 'currency-price' || inputs['currency-price']) {
							return inputs['currency-price'];
						} else {
							return this.getCurrencyById(inputs.currency)?.latest_exchange_record;
						}
					}
				},
				{
					text: this.$t('AccountingJournalPage.headers.paper-document-no'),
					name: 'paper-document-no',
					value: 'paper_document_no',
					width: '5%',
				},
				{
					text: this.$t('AccountingJournalPage.headers.date'),
					name: 'date',
					value: 'date',
					type: 'date',
					dateFormat: 'DD-MM-YYYY',
					width: 170,
					inputRules: [this.rules.required]
				},
				{
					text: '',
					name: 'hint',
					value: 'hint',
					width: 30,
					noNotAvailableText: true,
					divider: false,
					noInput: true, 
				},
			];

			const commonClass = 'font-weight-bold px-2 grey--text text--darken-4';
			for (let i = 0; i < headers.length; i++) {
				headers[i].columnClass =
					headers[i].columnClass ? headers[i] + ' ' + commonClass : commonClass;
				headers[i].align = 'center';
			}

			return headers
		},
		headers(){
			const headers = [
				// {
				// 	text: this.$t('AccountingJournalPage.headers.sequence'),
				// 	name: 'order-inside-bond',
				// 	value: 'order_inside_bond',
				// 	width: '5%',
				// 	noInput: true
				// },
				{
					text: '#',
					name: 'order-inside-bond',
					value: 'order_inside_bond',
					width: '2%',
					noInput: true
				},
				// {
				// 	text: this.$t('AccountingJournalPage.headers.debit'),
				// 	name: 'debit',
				// 	value: 'debit.amount',
				// 	type: 'number-format',
				// 	width: '8%',
				// 	inputRules: (() => {
				// 		if (this.inputsValidations.debit) return [];
				// 		else return [() => false];
				// 	})(),
				// },
				{
					text: this.$t('AccountingJournalPage.headers.debit'),
					name: 'debit',
					value: 'debit.amount',
					type: 'number-format',
					width: '9%',
					inputRules: (() => {
						if (this.inputsValidations.debit) return [];
						else return [() => false];
					})(),
				},
				// {
				// 	text: this.$t('AccountingJournalPage.headers.credit'),
				// 	name: 'credit',
				// 	value: 'credit.amount',
				// 	type: 'number-format',
				// 	width: '8%',
				// 	inputRules: (() => {
				// 		if (this.inputsValidations.credit) return [];
				// 		else return [() => false];
				// 	})(),
				// },
				{
					text: this.$t('AccountingJournalPage.headers.credit'),
					name: 'credit',
					value: 'credit.amount',
					type: 'number-format',
					width: '9%',
					inputRules: (() => {
						if (this.inputsValidations.credit) return [];
						else return [() => false];
					})(),
				},
				{
					text: this.$t('AccountingJournalPage.headers.account'),
					value: 'account_id',
					name: 'account',
					width: '16%',
					inputRules: [this.rules.required],
					setValueExpr: val => {
						const account = this.getAccountById(val);
						return `${account.accounting_no}-${account.name}`
					}
				},
				{
					text: this.$t('AccountingJournalPage.headers.note'),
					name: 'note',
					value: 'note',
					width: '17%',
				},
				// {
				// 	text: this.$t('AccountingJournalPage.headers.currency'),
				// 	name: 'currency',
				// 	value: 'currency_id',
				// 	width: 100,
				// 	setValueExpr: (value) => (this.getCurrencyById(value) || {}).symbol,
				// },
				{
					text: this.$t('AccountingJournalPage.headers.currency'),
					name: 'currency',
					value: 'currency_id',
					width: 95,
					setValueExpr: (value) => (this.getCurrencyById(value) || {}).symbol,
				},
				// {
				// 	text: this.$t('AccountingJournalPage.headers.currency-debit'),
				// 	name: 'currency-debit',
				// 	value: 'currency_debit.amount',
				// 	type: 'number-format',
				// 	width: this.$emerald.ifLang('es', '15%', '6%'),
				// 	inputRules: (() => {
				// 		if (this.inputsValidations['currency-debit']) return [];
				// 		else return [() => false]
				// 	})(),
				// 	setInputExpr: (inputs, activeInput) => {
				// 		if (activeInput !== 'currency-debit') {
				// 			if (inputs.currency > 0 && inputs.debit && inputs['currency-price']) {
				// 				this.tableInputs['currency-debit'] = inputs.debit / (inputs['currency-price'] || 1);
				// 			} else {
				// 				return 0;
				// 			}
				// 		} else {
				// 			if (inputs['currency-price'] && !inputs['currency-credit']) {
				// 				const newVal =
				// 					inputs['currency-debit'] ? inputs.debit / inputs['currency-debit'] : 0;
								
				// 				this.tableInputs['currency-price'] = newVal || inputs['currency-price'];
				// 				this.tableInputs.debit =
				// 					this.tableInputs['currency-price'] * inputs['currency-debit'];
				// 			}
				// 		}
				// 		return inputs['currency-debit'];
				// 	}
				// },
				{
					text: this.$t('AccountingJournalPage.headers.currency-debit'),
					name: 'currency-debit',
					value: 'currency_debit.amount',
					type: 'number-format',
					width: this.$emerald.ifLang('es', '15%', '9%'),
					inputRules: (() => {
						if (this.inputsValidations['currency-debit']) return [];
						else return [() => false]
					})(),
					setInputExpr: (inputs, activeInput) => {
						if (activeInput !== 'currency-debit') {
							if (inputs.currency > 0 && inputs.debit && inputs['currency-price']) {
								this.tableInputs['currency-debit'] = inputs.debit / (inputs['currency-price'] || 1);
							} else {
								return 0;
							}
						} else {
							if (inputs['currency-price'] && !inputs['currency-credit']) {
								const newVal =
									inputs['currency-debit'] ? inputs.debit / inputs['currency-debit'] : 0;
								
								this.tableInputs['currency-price'] = newVal || inputs['currency-price'];
								this.tableInputs.debit =
									this.tableInputs['currency-price'] * inputs['currency-debit'];
							}
						}
						return inputs['currency-debit'];
					}
				},
				// {
				// 	text: this.$t('AccountingJournalPage.headers.currency-credit'),
				// 	name: 'currency-credit',
				// 	value: 'currency_credit.amount',
				// 	type: 'number-format',
				// 	width: this.$emerald.ifLang('es', '15%', '6%'),
				// 	inputRules: (() => {
				// 		if (this.inputsValidations['currency-credit']) return [];
				// 		else return [() => false]
				// 	})(),
				// 	setInputExpr: (inputs, activeInput) => {
				// 		if (activeInput !== 'currency-credit') {
				// 			if (inputs.currency > 0 && inputs.credit && inputs['currency-price']) {
				// 				this.tableInputs['currency-credit'] = inputs.credit / (inputs['currency-price'] || 1);
				// 			} else {
				// 				return 0;
				// 			}
				// 		} else {
				// 			if (inputs['currency-price'] && !inputs['currency-debit']) {
				// 				const newVal =
				// 					inputs['currency-credit'] ? inputs.credit / inputs['currency-credit'] : 0;
								
				// 				this.tableInputs['currency-price'] = newVal || inputs['currency-price'];

				// 				this.tableInputs.credit =
				// 					this.tableInputs['currency-price'] * inputs['currency-credit'];
				// 			}
				// 		}
				// 		return inputs['currency-credit'];
				// 	}
				// },
				{
					text: this.$t('AccountingJournalPage.headers.currency-credit'),
					name: 'currency-credit',
					value: 'currency_credit.amount',
					type: 'number-format',
					width: this.$emerald.ifLang('es', '15%', '9%'),
					inputRules: (() => {
						if (this.inputsValidations['currency-credit']) return [];
						else return [() => false]
					})(),
					setInputExpr: (inputs, activeInput) => {
						if (activeInput !== 'currency-credit') {
							if (inputs.currency > 0 && inputs.credit && inputs['currency-price']) {
								this.tableInputs['currency-credit'] = inputs.credit / (inputs['currency-price'] || 1);
							} else {
								return 0;
							}
						} else {
							if (inputs['currency-price'] && !inputs['currency-debit']) {
								const newVal =
									inputs['currency-credit'] ? inputs.credit / inputs['currency-credit'] : 0;
								
								this.tableInputs['currency-price'] = newVal || inputs['currency-price'];

								this.tableInputs.credit =
									this.tableInputs['currency-price'] * inputs['currency-credit'];
							}
						}
						return inputs['currency-credit'];
					}
				},
				{
					text: this.$t('AccountingJournalPage.headers.price'),
					name: 'currency-price',
					value: 'currency_price',
					width: '8%',
					type: 'number-format',
					settings: { itemText: 'equivalentPrice', context: 'entry', actionName: this.actions.GET },
					inputRules: (() => {
						if (this.inputsValidations['currency-price']) return [];
						else return [() => false];
					})(),
					setInputExpr: (inputs, activeInput) => {
						if (activeInput === 'currency-price') {
							if (inputs.currency > 0) {
								if (inputs.debit && inputs['currency-debit']) {
									this.tableInputs.debit = inputs['currency-price'] * inputs['currency-debit'];
								} else if (inputs.credit && inputs['currency-credit']) {
									this.tableInputs.credit = inputs['currency-price'] * inputs['currency-credit'];
								}
							}
						}
						return inputs['currency-price'];
					}
				},
				{
					text: this.$t('AccountingJournalPage.headers.paper-document-no'),
					name: 'paper-document-no',
					value: 'paper_document_no',
					width: '5%',
				},
				// {
				// 	text: this.$t('AccountingJournalPage.headers.date'),
				// 	name: 'date',
				// 	value: 'date',
				// 	type: 'date',
				// 	// FIXME after replacing `bee-date-field` with `bee-date-input` you need format to be "DD-MM-YYYY"
				// 	dateFormat: 'YYYY/MM/DD',
				// 	width: 170,
				// 	inputRules: [this.rules.required]
				// },
				{
					text: this.$t('AccountingJournalPage.headers.date'),
					name: 'date',
					value: 'date',
					type: 'date',
					// FIXME after replacing `bee-date-field` with `bee-date-input` you need format to be "DD-MM-YYYY"
					dateFormat: 'YYYY/MM/DD',
					width: 120,
					inputRules: [this.rules.required]
				},
				{
					text: '',
					name: 'hint',
					value: 'hint',
					width: 30,
					noNotAvailableText: true,
					divider: false,
					noInput: true, 
				},
			];

			const commonClass = 'font-weight-bold px-2 grey--text text--darken-4';
			for (let i = 0; i < headers.length; i++) {
				headers[i].columnClass =
					headers[i].columnClass ? headers[i] + ' ' + commonClass : commonClass;
				headers[i].align = 'center';
			}

			return headers;
		},

		inputsValidations() {
			const inputs = this.tableInputs;
			return {
				debit: 
					(inputs['debit'] > 0 && !inputs['credit']) || (!inputs['debit'] && inputs['credit'] > 0),
				credit:
					(inputs['credit'] > 0 && !inputs['debit']) || (!inputs['credit'] && inputs['debit'] > 0),
				'currency-debit': 
					(!inputs['currency-debit'] && !inputs.currency) ||
					(inputs['currency-debit'] > 0 && inputs.currency > 0 && !inputs['credit']) ||
					(!inputs['currency-debit'] && inputs['currency-credit'] > 0 && inputs.currency > 0 && !inputs['debit']),
				'currency-credit': 
					(!inputs['currency-credit'] && !inputs.currency) ||
					(inputs['currency-credit'] > 0 && inputs.currency > 0 && !inputs['debit']) ||
					(!inputs['currency-credit'] && inputs['currency-debit'] > 0 && inputs.currency > 0 && !inputs['credit']),
				'currency-price':
					(!inputs.currency && !inputs['currency-price']) ||
					(inputs.currency && inputs['currency-price'])
			}
		},

		inputsValidationMessages() {
			const messages = [];
			const inputs = this.tableInputs;
			if (!inputs.debit && !inputs.credit) {
				messages.push(this.$t('table-messages.debit-and-credit-required'))
			}
			if (inputs.debit && inputs.credit) {
				messages.push(this.$t('table-messages.debit-and-credit-one-of'));
			}

			if (!inputs.account) {
				messages.push(this.$t('table-messages.account-required'));
			}
			
			if (!inputs.currency && (inputs['currency-debit'] || inputs['currency-credit'])) {
				messages.push(this.$t('table-messages.no-currency-debit-and-credit-without-currency'));
			}
			if (inputs.currency > 0 && !inputs['currency-debit'] && !inputs['currency-credit']) {
				messages.push(this.$t('table-messages.currency-debit-and-credit-required'));
			}
			if (inputs.debit && inputs['currency-credit']) {
				messages.push(this.$t('table-messages.debit-with-currency-debit-relation'));
			}
			if (inputs.credit && inputs['currency-debit']) {
				messages.push(this.$t('table-messages.credit-with-currency-credit-relation'));
			}

			if (inputs.currency > 0 && !inputs['currency-price']) {
				messages.push(this.$t('table-messages.currency-price-required'));
			}
			if (!inputs.currency && inputs['currency-price']) {
				messages.push(this.$t('table-messages.currency-required'));
			}

			// FIXME after replacing `bee-date-field` with `bee-date-input` you only need "!inputs.date" condition
			if (!inputs.date || this.validationMessages.includes('invalid-input-date')) {
				messages.push(this.$t('table-messages.date-required'));
			}
			return messages
		},
		
		contextmenuItems(){
			return [
				{
					title: this.$t('AccountingJournalPage.context-menu.account-statement'),
					event: 'account-statement',
					class: 'text-h6'
				},
				{
					title: this.$t('AccountingJournalPage.context-menu.edit'),
					event:'edit',
					disabled: this.bond.is_posted || this.file_is_locked,
					class: 'text-h6'
				},
				{
					title: this.$t('AccountingJournalPage.context-menu.delete'),
					event: 'delete',
					disabled: this.bond.is_posted || this.file_is_locked,
					class: 'text-h6'
				}
			]
		},

		// bond
		bond() {
			return this.accountingBond;
		},
		entries: function() {
			return this.bond.entries || [];
		},

		file_is_locked() {
			return this.configs?.financial_file?.is_locked;
		},

		// bond posting progress circular
		bond_posting_ratio_tooltip() {
			return this.configs?.num_of_not_posted_bonds
				? `${this.configs.num_of_not_posted_bonds} ${this.$t('AccountingJournalPage.not-posted-bonds')}`
				: this.$t('AccountingJournalPage.all-bonds-are-posted');
		},
		bond_posting_ratio() {
			return this.configs?.num_of_total_bonds
				? 100 -
						(this.configs.num_of_not_posted_bonds * 100) /
							this.configs.num_of_total_bonds
				: 100;
		},
		bond_posting_ratio_caption() {
			return `${this.configs?.num_of_not_posted_bonds}/${this.configs?.num_of_total_bonds}`;
		},

		// footer
		sum_debits() {
			return this.bond?.statement_summary?.sum_result?.sum_debits || null;
		},
		sum_credits() {
			return this.bond?.statement_summary?.sum_result?.sum_credits || null;
		},
		sum_difference() {
			return this.bond?.statement_summary?.sum_result?.sum_difference || null;
		},
		sum_difference_equivalent_api() {
			return (
				this.bond?.statement_summary?.sum_result?.sum_difference_equivalent ||
				null
			);
		},

		currency_debits() {
			return (
				this.bond?.statement_summary?.sum_currencies_result?.currency_debits ||
				null
			);
		},
		currency_credits() {
			return (
				this.bond?.statement_summary?.sum_currencies_result?.currency_credits ||
				null
			);
		},
		create_bonds_manually() {
			return ls.getAskBeforeBondCreation();
		},
		show_symbol_currencies() {
			return ls.getVisibleSymbolMoney();
		},

		// helpers
		on_lgAndUp() {
			if (this.$vuetify.breakpoint.lgAndUp) return true;
			return false;
		},
		on_mdAndUp() {
			if (this.$vuetify.breakpoint.mdAndUp) return true;
			return false;
		},

		...mapState({
			simplified_bonds: state => state.bonds.simplified_accounting_bonds,
			configs: state => state.configs.configs,
			currentFinancialFile: state => state.financialFiles.current_financial_file,
			not_posted_bonds: state => state.bonds.not_posted_bonds,
			/*Avoid sharing bond store data when starting a new financial file, if it hasn't had cash bonds yet.*/
			accountingBond: state => state.bonds.bond
		}),

		...mapGetters({
			getAccountById: 'configs/getAccountById',
			getCurrencyById: 'configs/getCurrencyById',
			isViewer: 'auth/isViewer',
		}),
	},

	methods: {
		inputsNavigationHandler (e, headerName) {
			if (e.shiftKey) return;

			if (headerName === 'debit' && this.tableInputs.debit) {
				e.preventDefault();
				this.$refs.table.focus('account');
			}
			else if (headerName === 'currency' && this.tableInputs.credit) {
				e.preventDefault();
				this.$refs.table.focus('currency-credit');
			}
			else if (headerName === 'currency-debit' && this.tableInputs['currency-debit']) {
				e.preventDefault();
				this.$refs.table.focus('currency-price');
			}
		},

		entryHandler() {
			if (this.$refs.table.validate()) {
				this.loading = true;
				const actionName = !this.entryId ? 'createEntry' : 'updateEntry' ;
				this.$store.dispatch(`bonds/${actionName}`, {
					bondId: this.bond.id,
					bondType: bondType.accounting,
					entry: {
						id: this.entryId,
						account_id: this.tableInputs.account,
						bond_id: this.bond.id,
						debit: this.tableInputs.debit ? {
							// amount: this.tableInputs.debit,
							amount: this.tableInputs['currency-price']
								? this.tableInputs['currency-price'] * this.tableInputs['currency-debit']
								: this.tableInputs.debit,
							currency_id: this.currentFinancialFile.default_currency_id
						} : null,
						credit: this.tableInputs.credit ? {
							// amount: this.tableInputs.credit,
							amount: this.tableInputs['currency-price']
								? this.tableInputs['currency-price'] * this.tableInputs['currency-credit']
								: this.tableInputs.credit,
							currency_id: this.currentFinancialFile.default_currency_id
						} : null,
						currency_debit: this.tableInputs['currency-debit'] ? {
							amount: this.tableInputs['currency-debit'],
							currency_id: this.tableInputs.currency
						} : null,
						currency_credit: this.tableInputs['currency-credit'] ? {
							amount: this.tableInputs['currency-credit'],
							currency_id: this.tableInputs.currency
						} : null,
						currency_price: this.tableInputs['currency-price'],
						note: this.tableInputs.note,
						date: this.tableInputs.date.slice(0, 10),
						paper_document_no: this.tableInputs['paper-document-no'],
					}
				}).then(() => {
					const initTable = () => {
						this.$nextTick(() => {
							this.$refs.table.scrollToRow();
							this.$refs.table.resetInputs({
								// FIXME replace `bee-date-field` with `bee-date-input`
								date:
									this.entries.length
										? this.entries[this.entries.length - 1].date.slice(0, 10)
										: this.tableInputs.date.slice(0, 10),
								note: this.saveNotes
							});
						})
					}
					
					if (this.entryId) {
						this.entryId = null;
						const nextRowIndex = this.$refs.table.editNextRow();
						if (nextRowIndex !== -1) {
							this.entryId = this.entries[nextRowIndex].id;
							this.$refs.table.scrollToRow();
						} else {
							initTable();
						}
					} else {
						initTable();
					}
					
					this.$nextTick(() => {
						this.$nextTick(() => {
							this.$nextTick(() => {
								if (this.entryId) {
									setTimeout(() => {
										this.$refs.table.resetInputsFocus();
									}, 100);
								}
								else {
									this.$refs.table.resetInputsFocus();
								}
							})
						})
					})
				}).finally(() => {
					this.loading = false;
				})
			}
		},

		confirmDeleteEntry() {
			this.loading = true;
			store.dispatch("bonds/deleteEntry", {
				bondId: this.bond.id,
				bondType: bondType.accounting,
				entryId: this.deleteEntryId
			})
			.then(() => {
				this.deleteEntryId = null;
				this.fetchBondByNo(this.bond_no)
			})
			this.$refs.table.resetEdit();
		},

		addBond() {
			this.loading = true;
			// this.simplified_bond.date = this.bond_date
			store.dispatch("bonds/create", {
				bondType: bondType.accounting,
				date: this.bond_date
			})
			.then(() => {
				this.bond_no = this.bond.bond_no;
				this.$router.push({ name: "accounting-journal", params: { id: this.bond.id } })
				this.$refs.table.endLineInputValues = {	date: new Date().toISOString() };
				this.$refs.table.onEsc();
				store.dispatch("configs/fetchAll")
			})
			.finally(() => (this.loading = false));

			this.addBondDialog = false;
			this.bond_date = new Date().toISOString().substr(0, 10).toString();
		},

		/* eslint-disable */
		fetchBond() {
			var bond = this.simplified_bonds?.find(c => c.bond_no == this.bond_no);
			if (bond && bond.id != Number(this.$route.params.id)) {
				this.$router.push({ name: "accounting-journal", params: { id: bond.id } })
				.then(() => {
					this.loading = true;
					store.dispatch("bonds/fetch", {
						bondId: Number(this.$route.params.id),
						bondType: bondType.accounting
					})
					.then(() => {
						this.$refs.table.resetEdit();
						this.$nextTick(() => {
							this.$refs.table.resetInputs({
								date: new Date().toISOString().slice(0, 10)
							});
						});
						this.loading = false;
					});
				});
			}
		},

		fetchBondByNo(bondNo) {
			if (bondNo) {
				this.loading = true;
				store.dispatch("bonds/fetchBondByNumber", {
					bondType: bondType.accounting,
					bondNo: bondNo
				})
				.then(() => {
					this.loading = false;
				});
			}
		},

		/* eslint-disable */
		posting() {
			this.updateBondLoading = true;
			store.dispatch("bonds/updateToPosted", {
				bondId: this.bond.id,
				bondType: bondType.accounting
			})
			.then(() => {
				Promise.all([
					store.dispatch("configs/fetchAll"),
					store.dispatch("bonds/fetch", {
						bondId: this.bond.id,
						bondType: bondType.accounting
					})
				]).finally(() => {
					this.updateBondLoading = false;
				});
			}).catch(() => { this.updateBondLoading = false; })
		},
		unPosting() {
			this.updateBondLoading = true;
			store.dispatch("bonds/updateToNonPosted", {
				bondId: this.bond.id,
				bondType: bondType.accounting
			})
			.then(() => {
				Promise.all([
					store.dispatch("configs/fetchAll"),
					store.dispatch("bonds/fetch", {
						bondId: this.bond.id,
						bondType: bondType.accounting
					})
				]).finally(() => {
					this.updateBondLoading = false;
				});;
			}).catch(() => { this.updateBondLoading = false; })
		},
		changeSumDifferenceEquivalent(toCurrencyId) {
			if (toCurrencyId) {
				if (this.sum_difference?.amount) {
					let amount = this.sum_difference.amount;
					let fromCurrecyId = this.sum_difference.currency_id;
					store.dispatch("currencies/exchangeToCurrency", {
						toCurrencyId,
						amount,
						fromCurrecyId
					})
					.then(response => {
						_.set(this.sum_difference_equivalent, "amount", response.amount);
						_.set(
							this.sum_difference_equivalent,
							"currency_id",
							response.currency_id
						);
						_.set(
							this.sum_difference_equivalent,
							"money_type",
							this.sum_difference_equivalent_api?.money_type
						);
					});
				}
			}
		},
		async createAccount() {
			this.showAccountForm = true;
			await store.dispatch("customStatements/fetchAll");
		},
		reForwardToRelatedInvoice() {
			store
				.dispatch("invoices/fetchInvoiceType", {
					invoiceId: this.bond.invoice_id
				})
				.then(response => {
					if (response == "Buy")
						this.$router.push({
							name: "invoices-buy",
							params: { id: this.bond.invoice_id }
						});
					else if (response == "Sale")
						this.$router.push({
							name: "invoices-sale",
							params: { id: this.bond.invoice_id }
						});
				});
		},

		openAccountStatement({ item }) {
			if (openNewWindowForAccountStatement) {
				window.open(this.$router.resolve({ name: 'statement', params: { id: item.account_id } }).href);
			} else {
				this.$router.push({ name: 'statement', params: { id: item.account_id } })
			}
		}
	},

	mounted() {
		this.loading = false;
		this.$nextTick(() => {
			this.$refs.table.resetInputs({
				date: new Date().toISOString().slice(0, 10)
			})
		})
		window.observer.observe(document.querySelector("#footer"));
	},
	async beforeRouteEnter(to, from, next) {
		let promises = [];

		let bonds = store.state.bonds.simplified_accounting_bonds;
		if (bonds.length == 0){
			promises.push(store.dispatch("bonds/fetchAll", { bondType: bondType.accounting }));
		}

		if (promises.length) { await Promise.all(promises) };

		if (to.params.id) {
			if (to.name != "accounting-journal"){
				next({
					name: "accounting-journal",
					params: { id: to.params.id },
					replace: true,
				});
			}
		} else {
			bonds = store.state.bonds.simplified_accounting_bonds;
			if (bonds.length > 0){
				next({
					name: "accounting-journal",
					params: { id: bonds[bonds.length - 1].id },
					replace: true,
				})
			};
		}
		next(vm => {
			if (to.name == "accounting-journal") {
				if (to.params.id) {
					vm.loading = true;
					store.dispatch("bonds/fetch", {
						bondId: Number(to.params.id),
						bondType: bondType.accounting
					})
					.then(response => {
						vm.loading = false;
						vm.bond_no = response.bond_no;
					});
				}
			}
		});
	}
};
</script>

<style scoped>
.medium-placeholder .v-label {
	font-size: 0.7rem !important;
}
.my-border {
	border: 1px solid !important;
	border-color: #f1f1f1 !important;
}
</style>
<style>
.journal .v-expansion-panel-content__wrap {
	padding: 0px !important;
	flex: 1 1 auto;
	max-width: 100%;
}
/* .v-toolbar__content {
	padding-left: 0px !important;
} */
</style>